import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit,} from '@angular/core';
import {TableLazyLoadEvent} from 'primeng/table';
import {AuthService} from '../../../../services/auth.service';
import {PermissionsEnum} from '../../../../permissions.enum';
import {WorkExperienceModel} from '../../../../model/employee/work-experience.model';
import {MenuItem} from 'primeng/api';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {DEFAULT_TABLE_ROWS_OPTIONS, LOCAL_STORAGE_WORKS_LIMIT, WORKS_LIMIT} from '../../../../constants';
import {WorkExperiencePopupComponent} from '../../../work-experience-popup/work-experience-popup.component';
import {DeletePopup} from '../../../delete-popup/delete-popup.component';
import {WORK_EXPERIENCE_DELETED} from '../../../../../../locale/multilingual-strings-constants';
import {DataConversionService} from '../../../../services/data-conversion.service';
import {TableFieldModel} from '../../../../model/table-field.model';
import {FieldTypesEnum} from '../../../../field-types.enum';
import {NotifierService} from '../../../../services/notifier.service';
import {PersonalDataService} from '../../../../../../../personal-data/src/lib/personal-data.service';
import {UtilService} from '../../../../services/util.service';

@Component({
  selector: 'app-work-experience',
  templateUrl: './work-experience.component.html',
  styleUrls: ['./work-experience.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkExperienceComponent implements OnInit {
  public workExperiences: WorkExperienceModel[];
  public items: MenuItem[];
  public ref: DynamicDialogRef | undefined;
  public totalRecords: number;
  public loading = true;
  public WORKS_LIMIT: number;
  public hasWriteWorkPermission = false;
  public hasDeleteWorkPermission = false;
  public selectedWork: WorkExperienceModel;
  public fields: TableFieldModel[] = [
    {
      name: 'organization',
      displayName: 'Organization',
      sortable: false,
      isCopy: false,
      globalFilter: false,
      searchable: false,
    },
    {
      name: 'occupation',
      displayName: 'Occupation',
      sortable: false,
      isCopy: false,
      globalFilter: false,
      searchable: false,
    },
    {
      name: 'contacts',
      displayName: 'Contacts',
      sortable: false,
      isCopy: false,
      globalFilter: false,
      searchable: false,
    },
    {
      name: 'from',
      displayName: 'From',
      sortable: false,
      isCopy: false,
      globalFilter: false,
      searchable: false,
      type: FieldTypesEnum.date,
    },
    {
      name: 'to',
      displayName: 'To',
      sortable: false,
      isCopy: false,
      globalFilter: false,
      searchable: false,
      type: FieldTypesEnum.date,
    },
  ];

  @Input() public userId: string;

  constructor(
    private notifierService: NotifierService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private dialogService: DialogService,
    private dataConversionService: DataConversionService,
    private personalDataService: PersonalDataService,
    private utilService: UtilService
  ) {
  }

  public ngOnInit(): void {
    const storedLimit = localStorage.getItem(LOCAL_STORAGE_WORKS_LIMIT);
    this.WORKS_LIMIT = storedLimit && DEFAULT_TABLE_ROWS_OPTIONS.includes(+storedLimit) ? +storedLimit : WORKS_LIMIT;
    this.hasWriteWorkPermission = this.authService.hasPermission(PermissionsEnum.writeWork, [PermissionsEnum.deleteWork]);
    this.hasDeleteWorkPermission = this.authService.hasPermission(PermissionsEnum.deleteWork);
    this.initWorkExperience();
    this.initMenuItems();
  }

  public openAddPopup() {
    this.openPopup('Add a new work experience', {personalDataId: this.userId});
  }

  private openPopup(header: string, data: { personalDataId?: string, workExperience?: WorkExperienceModel }) {
    this.ref = this.dialogService.open(WorkExperiencePopupComponent, {
      header,
      width: this.utilService.getPopupResponsiveWidth(),
      contentStyle: {overflow: 'auto'},
      baseZIndex: 10000,
      data,
      maximizable: true,
    });
    this.ref.onClose.subscribe((result: { edited: boolean; added: boolean; workExperience: WorkExperienceModel }) => {
      this.handlePopupResult(result);
    });
  }

  public handlePopupResult(result: { edited: boolean; added: boolean; workExperience: WorkExperienceModel }) {
    if (result) {
      if (result.edited) {
        const workExperience = this.workExperiences.find((workExperience: WorkExperienceModel) => workExperience.id === result.workExperience!.id)!;
        workExperience.organization = result.workExperience.organization;
        workExperience.occupation = result.workExperience.occupation;
        workExperience.contacts = result.workExperience.contacts;
        workExperience.from = result.workExperience.from;
        workExperience.to = result.workExperience.to;
        this.cdr.detectChanges();
      }
      if (result.added) {
        this.workExperiences.push(result.workExperience);
        this.totalRecords++;
        this.cdr.detectChanges();
      }
    }
  }

  public refresh() {
    this.initWorkExperience();
  }

  public initWorkExperience(event?: TableLazyLoadEvent) {
    this.loading = true;
    localStorage.setItem(LOCAL_STORAGE_WORKS_LIMIT, event?.rows?.toString() || WORKS_LIMIT.toString());
    this.personalDataService.getAllWorks(this.userId,
      (event ? event.first! / (event ? event.rows! : this.WORKS_LIMIT) + 1 : 1), (event ? event.rows! : this.WORKS_LIMIT)).subscribe({
      next: workExperienceResponse => {
        this.loading = false;
        if (workExperienceResponse.succeed) {
          if (!this.totalRecords && workExperienceResponse.value.totalCount) {
            this.totalRecords = workExperienceResponse.value.totalCount;
          }
          // if (workExperienceResponse.value.entities.length) {
          this.workExperiences = workExperienceResponse.value.entities.map(a => ({
            ...a,
          }));
          this.loading = false;
          this.cdr.detectChanges();
          // }
          this.cdr.detectChanges();
        } else {
          this.notifierService.handleErrors(workExperienceResponse.errors);
        }
      },
      error: err => this.notifierService.handleRequestError(err)
    });
  }

  public initMenuItems(workExperience?: WorkExperienceModel) {
    this.items = [];

    if (this.hasWriteWorkPermission) {
      this.items.push(
        {
          label: 'Edit',
          command: () => {
            workExperience && this.openPopup(workExperience.organization + ': User work experience',
              {workExperience: workExperience});
          }
        });
    }

    this.items.push({
      label: 'Copy',
      items: [
        {
          label: 'ID',
          command: () => {
            workExperience && this.utilService.copyText(workExperience.id as string, 'Work Experience ID copied.');
          }
        }
      ]
    })

    if (this.hasDeleteWorkPermission) {
      this.items.push(
        {
          label: 'Delete',
          command: () => {
            workExperience && this.openDeleteWorkExperiencePopup(workExperience);
          }
        });
    }
  }

  public initCmItems() {
    this.initMenuItems(this.selectedWork);
  }

  private openDeleteWorkExperiencePopup(workExperience: WorkExperienceModel) {
    this.ref = this.dialogService.open(DeletePopup, {
      header: 'Are you sure you want to delete this work experience information?',
      width: 'fit-content',
      contentStyle: {overflow: 'auto'},
      baseZIndex: 10000,
      data: {
        id: workExperience.id,
        data: this.dataConversionService.convertData(
          [workExperience],
          {
            organization: 'Organization',
            occupation: 'Occupation',
            contacts: 'Contacts',
            from: 'From',
            to: 'To',
          }
        )
      },
    });
    this.ref.onClose.subscribe(result => {
      if (result.delete) {
        this.deleteWorkExperience(workExperience, result.id);
      }
    });
  }

  public deleteWorkExperience(workExperience: WorkExperienceModel, workExperienceId: string) {
    this.personalDataService.deleteWork(workExperienceId).subscribe({
      next: r => {
        if (r.succeed) {
          this.notifierService.handleSuccessRequest(WORK_EXPERIENCE_DELETED);
          this.workExperiences.splice(this.workExperiences.indexOf(workExperience), 1);
          this.cdr.detectChanges();
        } else {
          this.notifierService.handleErrors(r.errors);
        }
      },
      error: err => {
        this.notifierService.handleRequestError(err);
      },
    });
  }

}
